import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { Country, CountryResponse } from '../models';
import * as CryptoJS from 'crypto-js';

const LOCATION_COOKIE_KEY = 'uc_loc';
const COOKIE_TTL_MS = 24 * 60 * 60 * 1000; // 24 horas
const COOKIE_SECRET = 'uc_s3cr3t_k3y_2024';

export interface UserCountry {
  country_id: number | string | null;
  user_ip: string | null;
  country?: Country;
}



@Injectable({ providedIn: 'root' })
export class CountryService {
  private http = inject(HttpClient);
  countries = signal<Country[]>([]);

  userCountry = signal<UserCountry>({
    country_id: null,
    user_ip: null,
    country: undefined
  });

  /**
   * Carga el país del usuario de forma asíncrona.
   *
   * Flujo:
   * 1. Espera a que el catálogo de países esté disponible (`loadCountries`).
   * 2. Intenta leer los datos de ubicación desde la cookie cifrada.
   *    - Si la cookie existe y tiene menos de 24 horas, hidrata `userCountry`
   *      directamente sin realizar ninguna petición HTTP.
   * 3. Si no hay cookie válida, realiza el GET al endpoint de geolocalización,
   *    actualiza `userCountry` y persiste la respuesta en la cookie cifrada.
   */
  async loadUserCountry(): Promise<void> {
    // Garantiza que el catálogo de países esté cargado antes de resolver
    // getCountryById, tanto en la rama de caché como en la de petición HTTP.
    await this.loadCountries();

    const cached = this.getLocationFromCookie();

    if (cached) {
      // Rama caché: los países ya están listos, se puede resolver el objeto Country.
      this.setUserCountry(cached);
      return;
    }

    // Rama HTTP: solicita la geolocalización al endpoint remoto.
    this.http.get<CountryResponse>(environment.locationEndPoint).subscribe({
      next: (response: CountryResponse) => {
        this.setUserCountry(response);
        // Persiste la respuesta en la cookie para evitar peticiones durante 24 h.
        this.saveLocationToCookie({
          country_id: response?.country_id ?? null,
          user_ip: response?.ip ?? null
        });
      },
      error: (err) => {
        console.error('Error obteniendo el país del usuario', err);
      }
    });
  }

  setUserCountry(userCountryData: any) {
    this.userCountry.set({
      country_id: userCountryData?.country_id ?? null,
      user_ip: userCountryData?.id ?? userCountryData?.user_ip ?? null,
      country: this.getCountryById(userCountryData?.country_id)
    });
  }

  /**
   * Reemplaza por completo el estado de `userCountry`.
   * Útil cuando el usuario selecciona manualmente su país.
   */
  updateUserCountry(country_id: string | number, user_ip: string, country: any) {
    this.userCountry.set({ country_id, user_ip, country });
  }

  /**
   * Actualiza parcialmente `userCountry` fusionando solo las propiedades
   * proporcionadas con el estado actual.
   */
  patchUserCountry(partial: Partial<{ country_id: string | number | null, user_ip: string | null, country: any }>) {
    this.userCountry.update(prev => ({ ...prev, ...partial }));
  }

  /**
   * Carga el catálogo de países desde el JSON estático de assets.
   * Elimina la primera entrada (país "internacional") y almacena el resultado
   * en el signal `countries`. Es idempotente: no vuelve a cargar si ya hay datos.
   */
  async loadCountries(): Promise<void> {
    if (this.countries.length === 0) {
      const countries = await firstValueFrom(this.http.get<Country[]>('/assets/countries.json'));
      countries.shift(); // Elimina el primer país (internacional)
      this.countries.set(countries);
    }
  }

  /**
   * Busca y devuelve un objeto `Country` por su `id`.
   * Utiliza igualdad débil (`==`) para tolerar tipos `number | string`.
   * @returns El país encontrado o `undefined` si no existe en el catálogo.
   */
  getCountryById(id: number | string | null): Country | undefined {
    return this.countries().find(c => c.id == id);
  }

  // ── Cookie helpers ────────────────────────────────────────────────────────

  /**
   * Cifra los datos de ubicación con AES y los persiste en una cookie
   * con expiración de `COOKIE_TTL_MS` (24 h). El campo `fetchedAt`
   * permite validar la antigüedad del dato al leer la cookie.
   */
  private saveLocationToCookie(data: { country_id: number | string | null; user_ip: string | null }): void {
    const payload = JSON.stringify({ ...data, fetchedAt: Date.now() });
    const encrypted = CryptoJS.AES.encrypt(payload, COOKIE_SECRET).toString();
    const expires = new Date(Date.now() + COOKIE_TTL_MS).toUTCString();
    document.cookie = `${LOCATION_COOKIE_KEY}=${encodeURIComponent(encrypted)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /**
   * Lee y descifra la cookie de ubicación.
   *
   * @returns Los datos `{ country_id, user_ip }` si la cookie existe y tiene
   *          menos de 24 horas, o `null` si no existe, expiró o está corrupta.
   *          En los dos últimos casos también elimina la cookie inválida.
   */
  private getLocationFromCookie(): { country_id: number | string | null; user_ip: string | null } | null {
    const match = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${LOCATION_COOKIE_KEY}=`));

    if (!match) return null;

    try {
      const encrypted = decodeURIComponent(match.split('=').slice(1).join('='));
      const bytes = CryptoJS.AES.decrypt(encrypted, COOKIE_SECRET);
      const payload = JSON.parse(bytes.toString(CryptoJS.enc.Utf8)) as {
        country_id: number | string | null;
        user_ip: string | null;
        fetchedAt: number;
      };

      // Verifica que la cookie no haya superado el TTL de 24 horas.
      if (Date.now() - payload.fetchedAt > COOKIE_TTL_MS) {
        this.deleteCookie(LOCATION_COOKIE_KEY);
        return null;
      }

      return { country_id: payload.country_id, user_ip: payload.user_ip };
    } catch {
      // Cookie corrupta o con clave de cifrado inválida: eliminarla.
      this.deleteCookie(LOCATION_COOKIE_KEY);
      return null;
    }
  }

  /** Elimina una cookie configurando su fecha de expiración en el pasado. */
  private deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}
